import Tier0Dashboard from '../components/dashboard/Tier0Dashboard';
import Tier1Dashboard from '../components/dashboard/Tier1Dashboard';
import Tier2Dashboard from '../components/dashboard/Tier2Dashboard';
import React, { useEffect, useMemo, useState } from 'react';
import Users from './Users';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import url from '../serverUrl';
import { api as axios } from '../api';
import AccountPanel from '../components/Tables/Exchange/pro/AccountPanel';
import { Logo } from '../components/Tables/Exchange/pro/PairPicker';
import MarketMarquee from '../components/Tables/Exchange/pro/MarketMarquee';
import { fmtQty, fmtUsd, num, connectWallet } from '../components/Tables/Exchange/pro/format';
import './dashboard.css';

// Account dashboard: identity header, portfolio tiles, allocation, the same
// balances / orders / history tables as the trade screen, then the existing
// tier tools (Vault, Wallet, Tokens, Profile; admin tiers keep theirs).

const DashboardIndex = ({ pecuCoins }) => {
  const { UserInfo, WalletInfo, auth_token } = useSelector((state) => state);
  const navigate = useNavigate();
  const [tier, setTier] = useState(UserInfo.tier || 2);
  const [tab] = useState(0);
  const [assets, setAssets] = useState(null);
  const [openOrders, setOpenOrders] = useState(null);

  useEffect(() => {
    if (!auth_token) return undefined;
    const headers = { Authorization: `Bearer ${auth_token}` };
    let cancelled = false;
    const load = () => {
      axios
        .get(`${url}/api/v2/wallet/balance`, { headers, timeout: 20000 })
        .then((res) => !cancelled && setAssets(res.data))
        .catch(() => {});
      axios
        .get(`${url}/api/v2/wallet/transactions`, { headers, timeout: 20000 })
        .then((res) => {
          if (cancelled) return;
          const p = res.data?.pending || {};
          const all = [...(p.bidOrderBook || []), ...(p.askOrderBook || [])];
          setOpenOrders(all.filter((o) => ['non-executed', 'executing'].includes(String(o.status))).length);
        })
        .catch(() => {});
    };
    load();
    const t = setInterval(load, 30000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [auth_token]);

  const switchTier = () => setTier((t) => (t < 2 ? t + 1 : 0));

  const holdings = useMemo(
    () =>
      [...(assets?.tokens || [])]
        .filter((t) => (num(t.value) || 0) > 0)
        .sort((a, b) => (num(b.value) || 0) - (num(a.value) || 0)),
    [assets]
  );
  const total = num(assets?.total) || holdings.reduce((s, t) => s + (num(t.value) || 0), 0);
  const pecu = holdings.find((t) => t.symbol === 'PECU') || assets?.tokens?.find((t) => t.symbol === 'PECU');
  const palette = ['#50d2c1', '#7aa7ff', '#c27cf6', '#f4a932', '#ed7088', '#3fb27f'];
  const top = holdings.slice(0, 5);
  const otherValue = holdings.slice(5).reduce((s, t) => s + (num(t.value) || 0), 0);
  const initial = (UserInfo?.username || UserInfo?.email || '?').slice(0, 1).toUpperCase();

  return (
    <div className="x-dash">
      <MarketMarquee />
      <section className="x-dash-head">
        <div className="x-dash-id">
          <span className="x-dash-avatar">{initial}</span>
          <div>
            <h1>{UserInfo?.username || 'My account'}</h1>
            <div className="x-muted">
              {UserInfo?.email}
              <span className="x-chip">Tier {tier}</span>
              <span className={WalletInfo ? 'x-chip x-chip-ok' : 'x-chip x-chip-off'}>
                {WalletInfo ? 'Wallet connected' : 'Wallet not connected'}
              </span>
            </div>
          </div>
        </div>
        <div className="x-dash-actions">
          {(UserInfo.tier === 0 || UserInfo.tier === 1) && (
            <button type="button" className="x-btn-ghost x-btn-sm" onClick={switchTier}>
              View as Tier {tier < 2 ? tier + 1 : 0}
            </button>
          )}
          {!WalletInfo && (
            <button type="button" className="x-btn-ghost x-btn-sm" onClick={connectWallet}>
              Connect Wallet
            </button>
          )}
          <a className="x-btn-ghost x-btn-sm" href="https://www.pecunovus.net/" target="_blank" rel="noreferrer">
            Deposit / Transfer
          </a>
          <button type="button" className="x-btn-primary x-btn-sm" onClick={() => navigate('/')}>
            Trade
          </button>
        </div>
      </section>

      <section className="x-dash-tiles">
        <div className="x-tile">
          <span className="x-tile-label">Portfolio value</span>
          <span className="x-tile-value">{assets ? fmtUsd(total) : '—'}</span>
          <span className="x-tile-sub">{holdings.length} asset{holdings.length === 1 ? '' : 's'}</span>
        </div>
        <div className="x-tile">
          <span className="x-tile-label">PECU balance</span>
          <span className="x-tile-value">{pecu ? fmtQty(pecu.amount) : assets ? '0' : '—'}</span>
          <span className="x-tile-sub">{pecu?.value ? `≈ ${fmtUsd(pecu.value)}` : ' '}</span>
        </div>
        <div className="x-tile">
          <span className="x-tile-label">Open orders</span>
          <span className="x-tile-value">{openOrders ?? '—'}</span>
          <span className="x-tile-sub">limit orders resting</span>
        </div>
        <div className="x-tile">
          <span className="x-tile-label">Largest holding</span>
          <span className="x-tile-value">{top[0] ? top[0].symbol : '—'}</span>
          <span className="x-tile-sub">
            {top[0] && total ? `${(((num(top[0].value) || 0) / total) * 100).toFixed(1)}% of portfolio` : ' '}
          </span>
        </div>
      </section>

      {holdings.length > 0 && (
        <section className="x-card x-dash-alloc">
          <div className="x-dash-sec-title">Allocation</div>
          <div className="x-alloc-bar">
            {top.map((t, i) => (
              <span
                key={t.symbol}
                style={{ width: `${((num(t.value) || 0) / total) * 100}%`, background: palette[i % palette.length] }}
                title={`${t.symbol} ${(((num(t.value) || 0) / total) * 100).toFixed(1)}%`}
              />
            ))}
            {otherValue > 0 && <span style={{ width: `${(otherValue / total) * 100}%`, background: '#2a3d45' }} />}
          </div>
          <div className="x-alloc-legend">
            {top.map((t, i) => (
              <span key={t.symbol}>
                <i style={{ background: palette[i % palette.length] }} />
                <Logo symbol={t.symbol} size={16} />
                <b>{t.symbol}</b>
                <span className="x-muted">{(((num(t.value) || 0) / total) * 100).toFixed(1)}%</span>
                <span>{fmtUsd(t.value, { compact: true })}</span>
              </span>
            ))}
            {otherValue > 0 && (
              <span>
                <i style={{ background: '#2a3d45' }} />
                <b>Other</b>
                <span className="x-muted">{((otherValue / total) * 100).toFixed(1)}%</span>
              </span>
            )}
          </div>
        </section>
      )}

      <section className="x-card x-dash-acct">
        <AccountPanel walletAssets={assets} auth_token={auth_token} market={null} />
      </section>

      <section className="x-dash-tools">
        <div className="x-dash-sec-title">Account tools</div>
        {tier === 0 && tab === 0 && <Tier0Dashboard user={UserInfo} pecuCoins={pecuCoins} />}
        {tier === 1 && tab === 0 && <Tier1Dashboard user={UserInfo} pecuCoins={pecuCoins} />}
        {tier === 2 && tab === 0 && <Tier2Dashboard user={UserInfo} pecuCoins={pecuCoins} />}
        {tab === 'users' && <Users user={UserInfo} />}
      </section>
    </div>
  );
};

export default DashboardIndex;
